/* ---------------------------------------------------------------------------
   The daily site return, and the safety record — the two things a construction
   report needs that are not activities.

   WHY NEITHER IS A PROGRESS ENTRY. src/data/activities.ts records what was
   built: an operation, at a place, on a date. Two of the things FIDIC
   Sub-Clause 4.21 asks for are not that shape at all:

     · (d) the Contractor's personnel and equipment — a statement about a DAY,
       written whether or not anything was built on it. A site that stood idle
       in the rain produced no pours and the fact that it stood idle is the
       most important thing in that week's report.

     · (g) safety statistics, hazardous incidents and environmental matters —
       a statement about an EVENT that is not work. An injury is not something
       that was constructed, and putting it in the activity library would make
       it one entry among twenty-nine pours.

   So they are two records of their own, and this file holds the vocabularies
   both need. The tables are migrations/0029 and 0030.

   ── WHAT IS DELIBERATELY NOT A CONTROLLED LIST ──

   **Trades and plant are free text, and that is a decision rather than
   laziness.** A trade breakdown — masons, steel fixers, carpenters, operators
   — varies by contractor, by country and by the method statement in force, and
   this project has no FWO method statement: the `pk-fwo` profile's own
   vocabulary is marked provisional for exactly that reason. A compiled trade
   list would be this repository's guess at somebody else's establishment,
   printed on a report they sign. What IS structured is the total head count,
   which is comparable across any taxonomy, and the breakdown travels beside it
   in the words the site uses.

   **Weather is the opposite case and IS a controlled list**, because it is not
   a description — it is a claim about whether work could proceed, and it is the
   evidence base for an extension-of-time claim. "Heavy rain" typed one way on
   Tuesday and another on Thursday cannot be counted; a list can.
   --------------------------------------------------------------------------- */

import { trimNumber } from './quantities.ts';
import type { SiteReturn } from './types.ts';

/* --------------------------- weather --------------------------- */

/**
 * The weather on site, as a day's return records it.
 *
 * SEVEN, ORDERED BY HOW MUCH THEY STOP WORK rather than alphabetically or by
 * temperature. The order is what a picker draws and what a report counts along:
 * "days lost to weather" is the sum of the tail of this list, and that only
 * means anything if the list is ordered by obstruction.
 *
 * `not-recorded` is first and is not an omission — see the note on it — but
 * it is no longer something the daily return's form offers: whoever writes the
 * day's return is on site and must say what the sky did (`saveSiteReturn` in
 * src/core/store.ts). It stays for returns already on record without one, and
 * for returns that arrive by import, which the register still accepts.
 */
export type Weather =
  | 'not-recorded'
  | 'clear'
  | 'overcast'
  | 'rain'
  | 'heavy-rain'
  | 'storm'
  | 'extreme-heat';

export interface WeatherOption {
  id: Weather;
  label: string;
  /**
   * Whether this is weather that ordinarily stops or curtails work.
   *
   * WHAT THE REPORT COUNTS, and the reason the list is ordered. It is a
   * property of the weather rather than a claim about a particular day: a site
   * may well pour in light rain and may well stand idle in sunshine for want of
   * materials. Whether work actually stopped is the `stoppage` activity, which
   * carries its own reason — this only says which days a reader should look at.
   */
  obstructive: boolean;
  note?: string;
}

export const WEATHER: readonly WeatherOption[] = [
  {
    id: 'not-recorded',
    label: 'Not recorded',
    obstructive: false,
    note: 'Nobody wrote it down. Counted as neither working weather nor lost weather.',
  },
  { id: 'clear', label: 'Clear', obstructive: false },
  { id: 'overcast', label: 'Overcast', obstructive: false },
  { id: 'rain', label: 'Rain', obstructive: false, note: 'Rain that did not in itself stop the works.' },
  { id: 'heavy-rain', label: 'Heavy rain', obstructive: true },
  { id: 'storm', label: 'Storm or high wind', obstructive: true },
  {
    id: 'extreme-heat',
    label: 'Extreme heat',
    obstructive: true,
    note: 'Heat that stops concreting or restricts working hours, which in both Nepal and Pakistan is a real cause of lost days.',
  },
];

const WEATHER_BY_ID = new Map(WEATHER.map((w) => [w.id, w]));

export function isWeather(value: unknown): value is Weather {
  return typeof value === 'string' && WEATHER_BY_ID.has(value as Weather);
}

/** Never empty, and never the raw id alone — the rule `activityLabel` keeps. */
export function weatherLabel(id: string): string {
  return WEATHER_BY_ID.get(id as Weather)?.label ?? `${id} (unknown)`;
}

/** Whether this is weather a report should count as having obstructed the works. */
export function weatherObstructs(id: string): boolean {
  return WEATHER_BY_ID.get(id as Weather)?.obstructive ?? false;
}

/* --------------------------- safety --------------------------- */

/**
 * What kind of thing happened.
 *
 * FIVE, AND THE FIRST TWO ARE THE ONES A MONTHLY REPORT IS READ FOR. A
 * lost-time injury is the statistic every client asks for; a near miss is the
 * one a good site records and a bad one does not, and the ratio between them is
 * the most informative number on the page.
 *
 * `environmental` is here rather than in a record of its own because 4.21(g)
 * asks for both in one breath and because a fuel spill and a cut hand are
 * reported through the same person on the same day. Splitting them would double
 * the machinery to separate two lists of three entries.
 */
export type IncidentKind =
  | 'near-miss'
  | 'first-aid'
  | 'lost-time'
  | 'dangerous-occurrence'
  | 'environmental';

export interface IncidentOption {
  id: IncidentKind;
  label: string;
  note: string;
  /** Whether this counts toward the lost-time statistic a client asks for. */
  lostTime: boolean;
  /** What the form's button reports it as: "Report a near miss". */
  reportAs: string;
  /** Whether the form asks who was involved, and how many. A near miss and a
   *  spill are about what happened, not to whom. */
  asksPeople: boolean;
  /** Whether the form asks for days lost — to a person, or to the works. */
  asksDaysLost: boolean;
}

export const INCIDENT_KINDS: readonly IncidentOption[] = [
  {
    id: 'near-miss',
    label: 'Near miss',
    note: 'Nobody was hurt and something could have been. The entry that makes the rest of this list believable.',
    lostTime: false,
    reportAs: 'near miss',
    asksPeople: false,
    asksDaysLost: false,
  },
  {
    id: 'first-aid',
    label: 'First aid injury',
    note: 'Treated on site; the person returned to work.',
    lostTime: false,
    reportAs: 'injury',
    asksPeople: true,
    asksDaysLost: false,
  },
  {
    id: 'lost-time',
    label: 'Lost-time injury',
    note: 'The person could not return to their next shift. This is the statistic a client asks for.',
    lostTime: true,
    reportAs: 'injury',
    asksPeople: true,
    asksDaysLost: true,
  },
  {
    id: 'dangerous-occurrence',
    label: 'Dangerous occurrence',
    note: 'A collapse, a dropped load, a failure of plant. Reportable whether or not anybody was hurt.',
    lostTime: false,
    reportAs: 'occurrence',
    asksPeople: true,
    asksDaysLost: true,
  },
  {
    id: 'environmental',
    label: 'Environmental',
    note: 'A spill, a discharge to the watercourse, a breach of a consent condition.',
    lostTime: false,
    reportAs: 'incident',
    asksPeople: false,
    asksDaysLost: false,
  },
];

const INCIDENT_BY_ID = new Map(INCIDENT_KINDS.map((k) => [k.id, k]));

export function isIncidentKind(value: unknown): value is IncidentKind {
  return typeof value === 'string' && INCIDENT_BY_ID.has(value as IncidentKind);
}

export function incidentLabel(id: string): string {
  return INCIDENT_BY_ID.get(id as IncidentKind)?.label ?? `${id} (unknown)`;
}

/** The kind's entry, or null for one not in the list (and for '' — none chosen). */
export function incidentOption(id: string): IncidentOption | null {
  return INCIDENT_BY_ID.get(id as IncidentKind) ?? null;
}

export function isLostTime(id: string): boolean {
  return INCIDENT_BY_ID.get(id as IncidentKind)?.lostTime ?? false;
}

/* --------------------------- the statistics --------------------------- */

/**
 * What a period's safety record adds up to.
 *
 * COUNTS AND NOT RATES. The statistic a client usually wants is a frequency —
 * lost-time injuries per hundred thousand hours worked — and this register
 * cannot honestly compute one: the denominator is hours worked by everybody on
 * site, and a daily return's `hours` is what the site chose to record, which is
 * not the same thing and is often absent. So the report prints the counts it
 * has, prints the hours it was given, and leaves the division to whoever knows
 * what the denominator really is.
 *
 * That is the same refusal `estimateFor` makes about a unit rate nobody
 * approved, and for the same reason: a number that looks authoritative and is
 * assembled from an unstated assumption is worse than no number.
 */
export interface SafetySummary {
  total: number;
  lostTime: number;
  byKind: Record<string, number>;
  /** Hours as the returns recorded them, or null where no return carried any. */
  hoursRecorded: number | null;
  /** How many returns in the period gave a figure for hours, of how many returns. */
  hoursFrom: { given: number; of: number };
}

/**
 * Add up a period's incidents, and report the hours WITHOUT dividing by them.
 *
 * Pure, and taking both lists rather than the database, so the report and any
 * later screen roll up the same way and this can be checked without one — the
 * same shape `summariseProgress` has.
 *
 * `hoursRecorded` is null where no return in the period gave a figure, which
 * is a different answer from zero and is the one the report prints a different
 * sentence for.
 */
export function safetySummary(
  incidents: readonly { kind: string }[],
  returns: readonly { hours: number | null }[],
): SafetySummary {
  const byKind: Record<string, number> = {};
  let lostTime = 0;

  for (const i of incidents) {
    byKind[i.kind] = (byKind[i.kind] ?? 0) + 1;
    if (isLostTime(i.kind)) lostTime += 1;
  }

  const given = returns.filter((r) => r.hours !== null);
  const hoursRecorded =
    given.length === 0 ? null : given.reduce((sum, r) => sum + (r.hours ?? 0), 0);

  return {
    total: incidents.length,
    lostTime,
    byKind,
    hoursRecorded,
    hoursFrom: { given: given.length, of: returns.length },
  };
}

/* --------------------------- the day's detail (LC-56) --------------------------- */

/** A return's people by trade, as the report prints them, or its note where it has no trades. */
export function tradesText(r: SiteReturn): string {
  const trades = r.labourByTrade ?? [];
  if (trades.length) return trades.map((t) => `${t.count} ${t.trade}`).join(', ');
  return r.labourNote || '—';
}

/** A return's plant, each with its hours working and idle, or its note where it lists none. */
export function plantText(r: SiteReturn): string {
  const plant = r.plantRows ?? [];
  if (plant.length) {
    return plant
      .map((p) => {
        const hours = [p.workingHours != null ? `${trimNumber(p.workingHours)} h working` : null, p.idleHours != null ? `${trimNumber(p.idleHours)} h idle` : null].filter(Boolean);
        return hours.length ? `${p.plant} (${hours.join(', ')})` : p.plant;
      })
      .join('; ');
  }
  return r.plant || '—';
}
